import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { EmployeeLayout } from '../../components/EmployeeLayout';
import { notificationAPI } from '../../lib/api';
import {
  Bell, CalendarPlus, CalendarX, CalendarClock, RefreshCw,
  Megaphone, FileText, DollarSign, MessageSquare, ShieldAlert, Headset,
  CheckCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// Map notification.type -> icon. Falls back to a generic bell.
const TYPE_ICON = {
  shift_assigned:        CalendarPlus,
  shift_updated:         CalendarClock,
  shift_cancelled:       CalendarX,
  shift_swap_open:       RefreshCw,
  shift_swap_accepted:   RefreshCw,
  replacement_request:   RefreshCw,
  announcement_posted:   Megaphone,
  announcement_created:  Megaphone,
  document_shared:       FileText,
  payslip_ready:         DollarSign,
  supervisor_message:    MessageSquare,
  security_alert:        ShieldAlert,
  support_reply:         Headset,
};

const TYPE_DEEPLINK = {
  shift_assigned:       '/employee/schedule',
  shift_updated:        '/employee/schedule',
  shift_cancelled:      '/employee/schedule',
  shift_swap_open:      '/shift-replacements',
  shift_swap_accepted:  '/shift-replacements',
  replacement_request:  '/shift-replacements',
  announcement_posted:  '/employee/announcements',
  announcement_created: '/employee/announcements',
  document_shared:      '/employee/documents',
  payslip_ready:        '/payslips',
  support_reply:        '/support',
};

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString();
}

export default function EmployeeNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await notificationAPI.getAll();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await notificationAPI.markRead(n.id);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    } catch {
      toast.error('Could not mark as read');
    }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await notificationAPI.markAllRead();
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Could not mark all as read');
    } finally {
      setBusy(false);
    }
  };

  const unreadCount = items.filter(x => !x.read).length;

  return (
    <EmployeeLayout>
      <div className="p-6 space-y-6" data-testid="employee-notifications">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Notifications
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {unreadCount === 0
                ? 'You are all caught up.'
                : `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAll}
              disabled={busy}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors min-h-[40px]"
              data-testid="notifications-mark-all-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
              Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Bell className="mx-auto text-slate-300 mb-3" size={40} />
            <p className="text-slate-500">No notifications yet.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {items.map(n => {
              const Icon = TYPE_ICON[n.type] || Bell;
              const deepLink = TYPE_DEEPLINK[n.type] || null;
              return (
                <li
                  key={n.id}
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all
                    ${n.read
                      ? 'border-slate-200'
                      : 'border-l-4 border-l-orange-500 border-t-slate-200 border-r-slate-200 border-b-slate-200'}
                  `}
                  data-testid={n.read ? 'notification-item-read' : 'notification-item-unread'}
                >
                  <div className="p-4 flex items-start gap-3">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                        ${n.read ? 'bg-slate-100 text-slate-400' : 'bg-orange-50 text-orange-600'}`}
                    >
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <p
                          className={`text-sm break-words ${n.read ? 'text-slate-700' : 'font-semibold text-slate-900'}`}
                          data-testid="notification-title"
                        >
                          {n.title || 'Notification'}
                        </p>
                        <span className="text-xs text-slate-400 flex-shrink-0 whitespace-nowrap">
                          {timeAgo(n.sent_at || n.created_at)}
                        </span>
                      </div>
                      {n.message && (
                        <p className="text-sm text-slate-600 mt-1 break-words" data-testid="notification-message">
                          {n.message}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        {deepLink && (
                          <Link
                            to={deepLink}
                            onClick={() => markRead(n)}
                            className="text-xs font-medium text-orange-600 hover:text-orange-700"
                            data-testid="notification-open-link"
                          >
                            Open →
                          </Link>
                        )}
                        {!n.read && (
                          <button
                            type="button"
                            onClick={() => markRead(n)}
                            className="text-xs font-medium text-slate-500 hover:text-slate-700"
                            data-testid="notification-mark-read-btn"
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </EmployeeLayout>
  );
}
